"use strict";
/**
 * Polling Service
 * Centralized management of orderbook and position polling
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ORDER_POLLING_INTERVAL_MS = void 0;
exports.startPollingForMarket = startPollingForMarket;
exports.stopAllPolling = stopAllPolling;
exports.getCurrentlyPollingMarketId = getCurrentlyPollingMarketId;
exports.isPollingMarket = isPollingMarket;
const orderbook_1 = require("../orderbook");
const order_page_positions_1 = require("../orders/order-page-positions");
const combined_position_1 = require("../orders/combined-position");
const permissions_1 = require("../permissions");
const state_1 = require("../state");
const ui_1 = require("../ui");
const orderbook_staleness_1 = require("./orderbook-staleness");
// Orderbook staleness at click time averages half this interval (plus RTT).
// The server serves these polls from its in-memory WS-backed cache, so
// polling faster is cheap; 125ms halves average staleness vs the old 200ms.
exports.ORDER_POLLING_INTERVAL_MS = 125;
const POSITION_POLLING_INTERVAL_MS = 500;
// Polling state
let stopOrderbookPolling = null;
let stopPositionPolling = null;
let currentlyPollingMarketId = null;
/** The market being polled: the selected one when it matches, else a stub. */
function pollingMarket(ticker, conditionId, clobTokenIds) {
    const state = (0, state_1.getState)();
    const selected = state.selectedMarket;
    if (selected && selected.id === ticker)
        return selected;
    return {
        id: ticker,
        venue: state.dataSource ?? "kalshi",
        title: ticker,
        subtitle: "",
        strike_price: 0,
        status: "active",
        bid: 0,
        ask: 0,
        conditionId,
        clobTokenIds,
    };
}
/**
 * Position polling for one market: the primary and its parallel legs in one
 * read per tick (ai/POSITION_CACHE_PLAN.md §5). A stale or unknown source marks
 * the display; `currentPosition`, which sells read, stays the primary's own and
 * is never replaced by an unknown one.
 */
function startPositionPollingForMarket(ticker, conditionId, clobTokenIds) {
    let isRunning = true;
    let timeoutId = null;
    let shown = null;
    const show = (yes, no, legs, warn) => {
        shown = { yes, no, legs };
        if (legs > 0 || warn)
            (0, ui_1.updatePositionDisplay)(yes, no, { legs, warn });
        else
            (0, ui_1.updatePositionDisplay)(yes, no);
    };
    const pollPosition = async () => {
        if (!isRunning)
            return;
        try {
            // Only attached parallel legs count, never additional markets
            // (ai/PARALLEL_LEGS_PLAN.md §2).
            const legs = (0, permissions_1.canParallelMarkets)() ? ((0, state_1.getState)().parallelMarkets ?? []) : [];
            const read = await (0, order_page_positions_1.readOrderPagePositions)(pollingMarket(ticker, conditionId, clobTokenIds), legs);
            if (!isRunning)
                return;
            if (read.primary) {
                if (read.legs.length > 0) {
                    const combined = (0, combined_position_1.combinePositions)(read.primary, read.legs);
                    const warn = read.primaryStale || combined.unknownLegs > 0 || combined.staleLegs > 0;
                    show(combined.yes, combined.no, combined.legs, warn);
                }
                else {
                    show(read.primary.yes_quantity || 0, read.primary.no_quantity || 0, 0, read.primaryStale);
                }
                (0, state_1.updateState)({ currentPosition: read.primary, lastPositionUpdate: Date.now() });
            }
            else {
                shown = null;
                (0, ui_1.displayUnknownPosition)();
            }
        }
        catch (error) {
            if (!isRunning)
                return;
            // Last numbers kept, marked: no overlay over the trading screen.
            console.error("Position polling error:", error);
            if (shown)
                show(shown.yes, shown.no, shown.legs, true);
            else
                (0, ui_1.displayUnknownPosition)();
        }
        if (isRunning) {
            timeoutId = window.setTimeout(pollPosition, POSITION_POLLING_INTERVAL_MS);
        }
    };
    pollPosition();
    return () => {
        isRunning = false;
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
    };
}
/**
 * Start polling for a specific market
 */
function startPollingForMarket(marketId, clobTokenIds) {
    console.log(`Starting orderbook polling for market: ${marketId}`);
    currentlyPollingMarketId = marketId;
    // Blank the previous market's prices; the monitor shows loading until the
    // first book.
    (0, ui_1.updateMarketBidAsk)(null, null);
    (0, orderbook_staleness_1.startStalenessMonitor)();
    // Get conditionId from selected market state (for Polymarket position filtering)
    const state = (0, state_1.getState)();
    const conditionId = state.selectedMarket?.conditionId;
    // Start orderbook polling
    stopOrderbookPolling = (0, orderbook_1.startProviderAwareOrderbookPolling)(marketId, clobTokenIds, exports.ORDER_POLLING_INTERVAL_MS, (data) => {
        if (data === null) {
            (0, ui_1.updateMarketBidAsk)(null, null);
            // No book yet (cold start / empty) — keep the indicator neutral.
            (0, orderbook_staleness_1.notifyOrderbookEmpty)();
            return;
        }
        (0, ui_1.updateMarketBidAsk)(data, (0, state_1.getState)().tradingSettings.shares);
        (0, orderbook_staleness_1.notifyOrderbookFresh)(data.serverAgeMs);
    }, (error) => {
        (0, ui_1.showOrderbookError)(error.message);
        (0, orderbook_staleness_1.notifyOrderbookError)(error.message);
    });
    // Start position polling
    console.log(`Starting position polling for market: ${marketId}`);
    stopPositionPolling = startPositionPollingForMarket(marketId, conditionId, clobTokenIds);
}
/**
 * Stop all polling
 */
function stopAllPolling() {
    if (stopOrderbookPolling) {
        console.log(`Stopping orderbook polling for market: ${currentlyPollingMarketId}`);
        stopOrderbookPolling();
        stopOrderbookPolling = null;
    }
    if (stopPositionPolling) {
        console.log(`Stopping position polling for market: ${currentlyPollingMarketId}`);
        stopPositionPolling();
        stopPositionPolling = null;
    }
    // Clear the freshness indicator back to neutral when polling stops.
    (0, orderbook_staleness_1.stopStalenessMonitor)();
    currentlyPollingMarketId = null;
}
/**
 * Get currently polling market ID
 */
function getCurrentlyPollingMarketId() {
    return currentlyPollingMarketId;
}
/**
 * Check if currently polling a specific market
 */
function isPollingMarket(marketId) {
    return currentlyPollingMarketId === marketId;
}
